import React, { useState } from 'react';
import { Menu, X, Shield } from 'lucide-react';

// Use favicon as logo or placeholder
const logo = '/images/logos/cf-logo-spaceman.png';

// AuthButtons removed - using Supabase auth now

interface HeaderProps {
  onContactClick?: () => void;
  onInvestorClick?: () => void;
}

export function Header({ onContactClick, onInvestorClick }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToAbout = () => {
    const aboutSection = document.querySelector('#social-media');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToGames = () => {
    const gamesSection = document.querySelector('#games');
    if (gamesSection) {
      gamesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle investor portal click
  const handleInvestorClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onInvestorClick) {
      onInvestorClick();
    } else {
      // Fallback - use global navigation if available
      const globalNav = (window as unknown as { CosmicInvestorNavigation?: { toInvestorPortal?: () => void } })
        .CosmicInvestorNavigation;
      if (globalNav?.toInvestorPortal) {
        globalNav.toInvestorPortal();
      } else {
        console.warn('🔒 Header: No investor portal navigation handler available');
      }
    }
    setIsMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', href: '#home', onClick: scrollToTop },
    { name: 'Games', href: '#games', onClick: scrollToGames },
    { name: 'About', href: '#social-media', onClick: scrollToAbout },
    { name: 'Contact', href: '#contact', onClick: onContactClick },
  ];

  // Special investor portal item (invitation-only access)
  const investorPortalItem = {
    name: 'Investor Portal',
    href: '#investor-portal',
    onClick: handleInvestorClick,
    icon: Shield,
    isSpecial: true
  };

  const handleNavClick = (item: typeof navItems[0], e: React.MouseEvent) => {
    e.preventDefault();
    if (item.onClick) {
      item.onClick();
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-cyan-500/20">
      <div className="container mx-auto px-4 py-2">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <a 
            href="#home" 
            className="transition-opacity duration-300 hover:opacity-80"
            onClick={(e) => {
              e.preventDefault();
              scrollToTop();
            }}
          >
            <img 
              src={logo} 
              alt="Cosmic Forces Logo" 
              className="h-6 sm:h-8 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Regular navigation items */}
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(item, e)}
                className="text-gray-300 hover:text-cyan-400 transition-colors duration-300 relative group font-cyberpunk-nav text-sm cursor-pointer"
              >
                {item.name}
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-600 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </a>
            ))}
            
            {/* Investor Portal - Special styling (invitation-only) */}
            <a
              href={investorPortalItem.href}
              onClick={investorPortalItem.onClick}
              className="text-purple-400 hover:text-purple-300 transition-colors duration-300 relative group font-cyberpunk-nav text-sm cursor-pointer flex items-center space-x-1 px-3 py-1 rounded border border-purple-500/20 hover:border-purple-500/40 hover:bg-purple-500/5"
              title="Invitation-only access for authorized investors"
            >
              <Shield className="h-4 w-4" />
              <span>INVESTOR PORTAL</span>
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-purple-400 to-cyan-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
            </a>
            
            {/* Auth buttons removed - using Supabase auth now */}
          </div>

          {/* Mobile Menu Button - Enhanced for better touch targets */}
          <button
            className="md:hidden text-gray-300 hover:text-cyan-400 transition-colors duration-300 p-2 -m-2 touch-manipulation"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? (
              <X size={28} className="drop-shadow-lg" />
            ) : (
              <Menu size={28} className="drop-shadow-lg" />
            )}
          </button>
        </nav>

        {/* Mobile Navigation - Enhanced for better touch experience */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-cyan-500/20">
            <div className="flex flex-col space-y-1 mt-4">
              {/* Regular nav items */}
              {navItems.map((item, index) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(item, e)}
                  className="text-gray-300 hover:text-cyan-400 active:text-cyan-300 transition-colors duration-300 text-lg font-cyberpunk-nav py-3 px-2 -mx-2 cursor-pointer touch-manipulation rounded-lg hover:bg-cyan-500/5 active:bg-cyan-500/10"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {item.name}
                </a>
              ))}
              
              {/* Mobile Investor Portal - Special styling (invitation-only) */}
              <a
                href={investorPortalItem.href}
                onClick={investorPortalItem.onClick}
                className="text-purple-400 hover:text-purple-300 active:text-purple-200 transition-colors duration-300 text-lg font-cyberpunk-nav py-3 px-2 -mx-2 cursor-pointer touch-manipulation rounded-lg hover:bg-purple-500/10 active:bg-purple-500/20 flex items-center space-x-2 border border-purple-500/20 mt-2"
                style={{ animationDelay: `${navItems.length * 0.1}s` }}
              >
                <Shield className="h-5 w-5" />
                <span>INVESTOR PORTAL</span>
                <div className="text-xs text-purple-400/60">(INVITE ONLY)</div>
              </a>
              
              {/* Mobile auth buttons removed - using Supabase auth now */}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}